import type { MarkAreaComponentOption } from "echarts/components";
import { withAlpha, type ChartColors } from "@nexus/widget-sdk";
import type { SeriesPeriod } from "../contracts/series-period";

/**
 * How a time chart shows the part of its period without data:
 * - `gap`: the axis keeps the whole period; uncovered spans are hatched "No data".
 * - `fit`: the axis is cropped to the data; gaps inside it stay hatched.
 * A missing value is never drawn as zero.
 */
export type MissingDataMode = "gap" | "fit";

export const NO_DATA_LABEL = "No data";

export function missingDataMode(value: unknown): MissingDataMode {
  return value === "fit" ? "fit" : "gap";
}

type MarkAreaData = NonNullable<MarkAreaComponentOption["data"]>;

export function periodBounds(period?: SeriesPeriod | null): { from: number; to: number } | null {
  const from = period ? Date.parse(period.from) : Number.NaN;
  const to = period ? Date.parse(period.to) : Number.NaN;
  return Number.isFinite(from) && Number.isFinite(to) && to > from ? { from, to } : null;
}

/** A bucket starting at or after the source's `dataThrough` has not been measured yet. */
export function afterDataThrough(startMs: number, dataThrough?: string | null): boolean {
  const through = dataThrough ? Date.parse(dataThrough) : Number.NaN;
  return Number.isFinite(through) && startMs >= through;
}

/** The UTC days (`YYYY-MM-DD`) a period touches. */
export function periodDays(period: { from: number; to: number }): string[] {
  const days: string[] = [];
  const cursor = new Date(period.from);
  cursor.setUTCHours(0, 0, 0, 0);
  while (cursor.getTime() < period.to && days.length < 3700) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

/** The UTC calendar months (`YYYY-MM`) a period touches. */
export function periodMonths(period: { from: number; to: number }): string[] {
  const months: string[] = [];
  const cursor = new Date(period.from);
  cursor.setUTCDate(1);
  cursor.setUTCHours(0, 0, 0, 0);
  while (cursor.getTime() < period.to && months.length < 130) {
    months.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months;
}

/** Runs of consecutive missing entries, as inclusive `[first, last]` indexes. */
export function missingRuns(missing: readonly boolean[]): [number, number][] {
  const runs: [number, number][] = [];
  missing.forEach((isMissing, index) => {
    if (!isMissing) return;
    const last = runs[runs.length - 1];
    if (last && last[1] === index - 1) last[1] = index;
    else runs.push([index, index]);
  });
  return runs;
}

/** The entries between the first and the last one with data ("Fit to data"). */
export function trimMissing<T>(items: readonly T[], isMissing: (item: T) => boolean): T[] {
  const first = items.findIndex((item) => !isMissing(item));
  if (first < 0) return [];
  const last = items.length - 1 - [...items].reverse().findIndex((item) => !isMissing(item));
  return items.slice(first, last + 1);
}

/**
 * Spans of a period without samples: before the first sample, after the last
 * one, and between two samples further apart than two steps. Shorter holes
 * are sampling jitter, not missing data.
 */
export function timeGaps(
  times: readonly number[],
  step: number,
  period?: { from: number; to: number } | null,
): [number, number][] {
  const sorted = [...times].sort((a, b) => a - b);
  const gaps: [number, number][] = [];
  const tolerance = step * 2;
  if (sorted.length === 0) return period ? [[period.from, period.to]] : [];
  if (period && sorted[0] - period.from > tolerance) gaps.push([period.from, sorted[0]]);
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index] - sorted[index - 1] > tolerance)
      gaps.push([sorted[index - 1] + step, sorted[index]]);
  }
  const end = sorted[sorted.length - 1] + step;
  if (period && period.to - end > tolerance) gaps.push([end, period.to]);
  return gaps;
}

const patterns = new Map<string, unknown>();

/** A light diagonal hatch, drawn once per colour; a flat tint where canvas is unavailable. */
function hatch(colors: ChartColors): string | object {
  const ink = withAlpha(colors.label, 0.22);
  const cached = patterns.get(ink);
  if (cached) return cached as object;
  const fallback = withAlpha(colors.label, 0.06);
  if (typeof document === "undefined") return fallback;
  const canvas = document.createElement("canvas");
  canvas.width = 8;
  canvas.height = 8;
  let context: CanvasRenderingContext2D | null = null;
  try {
    context = canvas.getContext("2d");
  } catch {
    context = null;
  }
  if (!context) return fallback;
  context.fillStyle = withAlpha(colors.label, 0.04);
  context.fillRect(0, 0, 8, 8);
  context.strokeStyle = ink;
  context.lineWidth = 1;
  context.beginPath();
  // Three strokes so the tile repeats without seams.
  for (const offset of [-8, 0, 8]) {
    context.moveTo(offset, 8);
    context.lineTo(offset + 8, 0);
  }
  context.stroke();
  const pattern = { image: canvas, repeat: "repeat" };
  patterns.set(ink, pattern);
  return pattern;
}

function band(colors: ChartColors, labelled: boolean) {
  return {
    name: NO_DATA_LABEL,
    itemStyle: { color: hatch(colors) as string, borderWidth: 0 },
    label: {
      show: labelled,
      position: "insideTop" as const,
      distance: 8,
      formatter: NO_DATA_LABEL,
      color: colors.label,
      fontSize: Math.max(11, (colors.typography?.labelSize ?? 12) - 1),
      fontFamily: colors.typography?.fontFamily,
    },
  };
}

/** Hatched "No data" bands over runs of missing categories (bars, days, weeks, months). */
export function categoryNoDataAreas(
  categories: readonly string[],
  missing: readonly boolean[],
  colors: ChartColors,
): MarkAreaData {
  return missingRuns(missing).map(([first, last]) => [
    { ...band(colors, true), xAxis: categories[first] },
    { xAxis: categories[last] },
  ]);
}

/** Hatched "No data" bands over time spans; only spans wide enough carry the label. */
export function timeNoDataAreas(
  gaps: readonly [number, number][],
  colors: ChartColors,
  span: number,
): MarkAreaData {
  return gaps.map(([from, to]) => [
    { ...band(colors, span > 0 && (to - from) / span >= 0.06), xAxis: from },
    { xAxis: to },
  ]);
}
